import React, { useState, useMemo } from 'react';
import ScenarioGraph from './ScenarioGraph';
import { Icon } from '../../../components';
import { useSteps } from '../../../hooks/useSteps';
import { FORMS } from '../../../schemas/stepMap';
import ProjectionTable from './ProjectionTable/ProjectionTable';
import { projectionCalculator } from './projectionCalculator';
import graphDataFormatter from './graphDataFormatter';
import BackForwardButtons from '../../../components/buttons/BackForwardButtons';
import { useProperty } from '../../../hooks/useProperty';

const graphSelectOptions = {
  'Net Monthly Income': ['monthlyNOI', 'monthlyNOI', 'netMonthlyIncome'],
  'Total Equity': ['totalEquity', 'totalEquity', 'equityInProperty'],
  'Cash Flow': [
    'annualPreTaxCashFlow',
    'monthlyPreTaxCashFlowBuyAndHold',
    'newMonthlyPreTaxCashFlow',
  ],
  ROI: [
    'totalRoiYear1Amount',
    'totalROIYear1BuyAndHoldAmount',
    'newTotalRoiYear1',
  ],
  'Property Value': [
    'marketPurchasePrice',
    'propertyValueProjection',
    'newMarketValue',
  ],
};

export default function Comparison() {
  const { propertyData, scenarios } = useProperty();
  const scenarioData = scenarios.find((scenario) => !scenario.isDefault);

  const { prev, next } = useSteps();
  const [years, setYears] = useState(5);
  const [graph, setGraph] = useState('Net Monthly Income');

  const onGraphSelect = (e) => {
    e.preventDefault();
    setGraph(e.target.value);
  };
  const onYearSelect = (e) => {
    e.preventDefault();
    setYears(e.target.value);
  };

  const projectionForm = FORMS.projection;

  const purchaseSchema = projectionForm.schema.calculations.schema[0];
  const brrrrSchema = projectionForm.schema.calculations.schema[1];
  const buyAndHoldSchema = projectionForm.schema.calculations.schema[2];
  const purchaseFields = purchaseSchema.schema;
  const brrrrFields = brrrrSchema.schema;
  const buyAndHoldFields = buyAndHoldSchema.schema;

  const rehabYears = scenarioData?.rehabLength
    ? Math.floor(scenarioData?.rehabLength / 12)
    : 0;

  const buyAndHoldData = useMemo(
    () =>
      projectionCalculator(years, buyAndHoldFields, {
        ...propertyData,
        ...scenarioData,
      }),
    [years, propertyData, scenarioData]
  );

  const purchaseData = useMemo(
    () => projectionCalculator(years, purchaseFields, propertyData),
    [years, propertyData, scenarioData]
  );

  const postBRRRRData = useMemo(
    () =>
      projectionCalculator(
        years,
        brrrrFields,
        {
          ...propertyData,
          ...scenarioData,
        },
        rehabYears
      ),
    [years, propertyData, scenarioData]
  );

  const preBRRRRData = rehabYears
    ? projectionCalculator(rehabYears, buyAndHoldFields, {
        ...propertyData,
        ...scenarioData,
      })
    : null;

  const brrrrData = preBRRRRData
    ? preBRRRRData.concat(postBRRRRData)
    : postBRRRRData;

  const graphData = graphDataFormatter(
    years,
    purchaseData,
    buyAndHoldData,
    brrrrData,
    graphSelectOptions[graph][0],
    graphSelectOptions[graph][1] || null,
    graphSelectOptions[graph][2] || null
  );

  return (
    <>
      <div className="pt-8 sm:pt-10">
        <div className="w-full flex flex-row gap-4 items-center justify-center text-md pb-2">
          <select
            name="time"
            id="time"
            aria-label="choose time frame"
            onChange={onYearSelect}
            className="text-md rounded-md shadow-sm border-neutral-300 py-1 max-w-lg block focus:ring-primary focus:border-primary text-black"
          >
            <option value="5">5 Years</option>
            <option value="10">10 Years</option>
            <option value="15">15 Years</option>
            <option value="30">30 Years</option>
            <option value="50">50 Years</option>
          </select>
          <select
            name="graph"
            id="graph"
            aria-label="choose which metric to graph"
            onChange={onGraphSelect}
            className="text-md rounded-md shadow-sm border-neutral-300 py-1 max-w-lg block focus:ring-primary focus:border-primary text-black"
          >
            {Object.keys(graphSelectOptions).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center justify-center gap-4 md:gap-9 mt-3 mb-2 text-md text-center text-black">
          <div className="flex items-center gap-1">
            <h3>On Purchase </h3>
            <Icon icon="IoMdSquare" classes="text-neutral-300 text-xl" />
          </div>
          <div className="flex items-center gap-1">
            <h3>Buy and Hold</h3>
            <Icon icon="IoMdSquare" classes="text-[#15803D] text-xl" />
          </div>
          <div className="flex items-center gap-1">
            <h3>BRRRR Scenario </h3>
            <Icon icon="IoMdSquare" classes="text-primary text-xl" />
          </div>
        </div>
        <div className="flex items-center -ml-14">
          <ScenarioGraph scenarios={scenarios} data={graphData} label={graph} />
        </div>
      </div>

      <div className="mt-8 text-base flex flex-col">
        <ProjectionTable
          data={purchaseData}
          sectionSchema={purchaseSchema}
          label="On Purchase"
          className="mb-4"
          propertyData={propertyData}
        />

        <ProjectionTable
          data={buyAndHoldData}
          sectionSchema={buyAndHoldSchema}
          label="Buy and Hold"
          className="mb-4"
          rehabYears={rehabYears}
          propertyData={propertyData}
        />

        <ProjectionTable
          data={brrrrData}
          sectionSchema={brrrrSchema}
          label="Brrrr Scenario"
          className="mb-4"
          rehabYears={rehabYears}
          propertyData={propertyData}
        />

        <BackForwardButtons prev={prev} next={next} />
      </div>
    </>
  );
}
